/**
 * Material overlay for the design editor (e.g. wood planks over a photo).
 *
 * Configured per product in `Product.specifications.editorMaterialOverlay`, in the
 * same shape as `priceMatrix`: the selected values of `keyFields` are joined with
 * `|` and looked up in `overlays`.
 *
 * ```json
 * {
 *   "keyFields": ["size", "orientation", "material", "plank_direction"],
 *   "overlays": {
 *     "30x20|landscape|naturel|vertical": "/editor-overlays/vurenhout/30x20-naturel-verticaal.webp"
 *   }
 * }
 * ```
 *
 * The image covers the trim area (the finished size, without bleed). It is only a
 * preview of the material: the editor draws it as a separate layer that is never
 * part of the print file. A configuration without an entry gets no overlay.
 */
export interface EditorMaterialOverlaySpec {
  keyFields: string[];
  overlays: Record<string, string>;
}

const SAFE_OVERLAY_URL = /^\/(?!\/)[A-Za-z0-9._~\-/]+\.(webp|png|avif|jpe?g)$/i;

export function resolveEditorMaterialOverlayUrl(
  specifications: unknown,
  configuration: Record<string, unknown> | null | undefined,
): string | null {
  const spec = (specifications as { editorMaterialOverlay?: unknown } | null | undefined)?.editorMaterialOverlay as
    | Partial<EditorMaterialOverlaySpec>
    | undefined;
  if (!spec || typeof spec !== 'object' || !configuration) return null;
  const { keyFields, overlays } = spec;
  if (!Array.isArray(keyFields) || keyFields.length === 0 || !overlays || typeof overlays !== 'object') return null;

  const parts: string[] = [];
  for (const field of keyFields) {
    if (typeof field !== 'string') return null;
    const value = configuration[field];
    if (value === undefined || value === null || value === '') return null;
    parts.push(String(value));
  }

  const url = (overlays as Record<string, unknown>)[parts.join('|')];
  // Only site-relative image paths: the URL ends up in an <img src>.
  return typeof url === 'string' && SAFE_OVERLAY_URL.test(url) ? url : null;
}
